import { create } from "zustand";
import { Profile } from "@/types/profile";
import { apiRequest } from "@/utils/apiClient";

interface AuthState {
  profile: Profile | null;
  loading: boolean;
  hasFetched: boolean;
  fetchProfile: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  profile: null,
  loading: false,
  hasFetched: false,
  fetchProfile: async () => {
    if (get().loading) {
      return;
    }
    set({ loading: true });
    try {
      const { profile } = await apiRequest<{ profile: Profile }>("/api/admin/profile");
      set({ profile, loading: false, hasFetched: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (message && !["Invalid credentials", "Missing credentials", "Token expired", "Invalid token"].includes(message)) {
        console.error("Failed to load profile", error);
      }
      set({ profile: null, loading: false, hasFetched: true });
    }
  },
  login: async (username: string, password: string) => {
    set({ loading: true });
    try {
      await apiRequest("/api/admin/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      const { profile } = await apiRequest<{ profile: Profile }>("/api/admin/profile");
      set({ profile, loading: false, hasFetched: true });
    } catch (error) {
      set({ loading: false, hasFetched: true });
      throw error instanceof Error ? error : new Error("Login failed");
    }
  },
  logout: async () => {
    try {
      await apiRequest("/api/admin/auth/logout", { method: "POST" });
    } catch (error) {
      console.error("Failed to log out", error);
    } finally {
      set({ profile: null, hasFetched: true, loading: false });
    }
  },
}));
